import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';

const API = 'https://tribaltech-backend.onrender.com';

function dollars(cents, currency = 'usd') {
  const symbol = currency.toLowerCase() === 'usd' ? '$' : '';
  return symbol + (cents / 100).toFixed(2);
}

export default function MyPurchases() {
  const navigate = useNavigate();
  const [items, setItems]  = useState([]);
  const [subs, setSubs]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr]      = useState('');
  const [busyId, setBusyId] = useState(null);

  const token = localStorage.getItem('vault_token');
  const email = localStorage.getItem('vault_email') || '';

  useEffect(() => {
    if (!token) { navigate('/vault/login', { replace: true }); return; }
    const headers = { Authorization: 'Bearer ' + token };
    Promise.all([
      axios.get(`${API}/api/vault/purchases`, { headers }),
      axios.get(`${API}/api/subscriptions/mine`, { headers }).catch(() => ({ data: { subscriptions: [] } })),
    ]).then(([p, s]) => {
      setItems(p.data.purchases || []);
      setSubs(s.data.subscriptions || []);
    }).catch(e => {
      if (e.response?.status === 401) {
        localStorage.removeItem('vault_token');
        localStorage.removeItem('vault_email');
        navigate('/vault/login', { replace: true });
        return;
      }
      setErr(e.response?.data?.error || e.message);
    }).finally(() => setLoading(false));
  }, [token, navigate]);

  async function cancelSubscription(subId) {
    if (!window.confirm('Cancel this subscription? You\'ll keep access until the end of the current billing period.')) return;
    try {
      await axios.post(`${API}/api/subscriptions/${subId}/cancel`, {}, {
        headers: { Authorization: 'Bearer ' + token },
      });
      const r = await axios.get(`${API}/api/subscriptions/mine`, { headers: { Authorization: 'Bearer ' + token } });
      setSubs(r.data.subscriptions || []);
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    }
  }

  function signOut() {
    localStorage.removeItem('vault_token');
    localStorage.removeItem('vault_email');
    navigate('/vault/login', { replace: true });
  }

  async function openVideo(purchaseId, mode) {
    setBusyId(purchaseId + ':' + mode);
    try {
      const r = await axios.get(`${API}/api/vault/purchases/${purchaseId}/download`, {
        params: { mode },
        headers: { Authorization: 'Bearer ' + token },
      });
      if (r.data?.url) {
        window.open(r.data.url, '_blank', 'noopener,noreferrer');
      }
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div style={page}>
      <div style={{ maxWidth: 1100, margin:'0 auto', padding:'40px 20px' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 30 }}>
          <div>
            <div style={eyebrow}>Creator Protect · Vault</div>
            <h1 style={{ color:'#fff', fontSize: 28, margin:'4px 0 2px' }}>My purchases</h1>
            <div style={{ color:'#94a3b8', fontSize: 13 }}>Signed in as {email}</div>
          </div>
          <button onClick={signOut} style={signOutBtn}>Sign out</button>
        </div>

        {loading && <div style={{ color:'#888' }}>Loading…</div>}
        {err && <div style={{ color:'#f87171' }}>{err}</div>}

        {subs.length > 0 && (
          <div style={{ marginBottom: 30 }}>
            <div style={{ color:'#a78bfa', fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Subscriptions</div>
            <div style={{ display:'flex', flexDirection:'column', gap: 10 }}>
              {subs.map(s => {
                const isActive = ['active', 'past_due'].includes(s.status);
                const periodEnd = s.current_period_end ? new Date(s.current_period_end) : null;
                return (
                  <div key={s.id} style={{ background:'#111', border:'1px solid #222', borderRadius: 12, padding: 16, display:'flex', alignItems:'center', gap: 16 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ color:'#fff', fontSize: 15, fontWeight: 600, marginBottom: 2 }}>
                        @{s.creator_username || '(unknown creator)'}
                      </div>
                      <div style={{ color:'#94a3b8', fontSize: 12 }}>
                        ${(s.price_cents / 100).toFixed(2)}/mo · status: {s.status}
                        {s.cancel_at_period_end && periodEnd && ` · ends ${periodEnd.toLocaleDateString()}`}
                        {!s.cancel_at_period_end && periodEnd && ` · renews ${periodEnd.toLocaleDateString()}`}
                      </div>
                    </div>
                    {isActive && (
                      <Link to={`/vault/${encodeURIComponent(s.creator_username || '')}`}
                            style={{ padding:'8px 14px', borderRadius: 8, background:'#7c3aed', color:'#fff', textDecoration:'none', fontSize: 12, fontWeight: 600 }}>
                        Visit storefront
                      </Link>
                    )}
                    {isActive && !s.cancel_at_period_end && (
                      <button onClick={() => cancelSubscription(s.id)}
                              style={{ padding:'8px 14px', borderRadius: 8, background:'transparent', border:'1px solid #333', color:'#94a3b8', fontSize: 12, cursor:'pointer' }}>
                        Cancel
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {!loading && !err && items.length === 0 && subs.length === 0 && (
          <div style={emptyBox}>
            Nothing here yet. When PPV launches, your purchases will appear here as soon as the delivery copy is ready (a minute or two after checkout).
          </div>
        )}

        {items.length > 0 && (
          <div style={{ color:'#a78bfa', fontSize: 14, fontWeight: 600, marginBottom: 12 }}>One-time purchases</div>
        )}
        <div style={grid}>
          {items.map(item => (
            <PurchaseCard
              key={item.purchase_id}
              item={item}
              busyId={busyId}
              onOpen={openVideo}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

const SHORT_VIDEO_MAX_SECONDS = 600; // 10 min

function PurchaseCard({ item, busyId, onOpen }) {
  const { purchase_id, status, listing, amount_cents, currency, purchased_at } = item;
  const duration   = listing?.duration_seconds || 0;
  const isShort    = duration > 0 && duration < SHORT_VIDEO_MAX_SECONDS;
  const buyerReady = status === 'delivered';
  const baseReady  = listing?.base_watermark_status === 'ready';
  // Short videos wait for the buyer-specific copy. Long videos
  // can play from the creator-watermarked base while the buyer
  // pipeline finishes in the background — download endpoint
  // returns whichever is ready.
  const playable = buyerReady || (baseReady && !isShort);
  const personalizing = !buyerReady && baseReady && !isShort && status !== 'failed';

  const title = listing?.title || 'Untitled';
  const previewUrl = listing?.preview_url;

  return (
    <div style={card}>
      <div style={thumbWrap}>
        {previewUrl ? (
          <video src={previewUrl} muted playsInline preload="metadata" style={thumb} />
        ) : (
          <div style={{ ...thumb, display:'flex', alignItems:'center', justifyContent:'center', color:'#555' }}>
            No preview
          </div>
        )}
        {!playable && (
          <div style={badge}>
            {status === 'failed'       ? 'Watermark failed' :
             status === 'paid'         ? 'Queued…' :
             status === 'watermarking' ? 'Preparing your copy…' :
             status}
          </div>
        )}
        {personalizing && (
          <div style={{ ...badge, background:'rgba(124,58,237,0.18)', color:'#a78bfa' }}>
            Personalizing…
          </div>
        )}
      </div>
      <div style={{ padding: 16 }}>
        <div style={{ color:'#fff', fontSize: 15, fontWeight: 600, marginBottom: 4 }}>{title}</div>
        <div style={{ color:'#94a3b8', fontSize: 12, marginBottom: 14 }}>
          {dollars(amount_cents, currency)} · {new Date(purchased_at).toLocaleDateString()}
        </div>
        <div style={{ display:'flex', gap: 8 }}>
          <button
            disabled={!playable || busyId === `${purchase_id}:watch`}
            onClick={() => onOpen(purchase_id, 'watch')}
            style={{ ...btn, background: playable ? '#7c3aed' : '#333' }}
          >
            {busyId === `${purchase_id}:watch` ? '…' : 'Watch'}
          </button>
          <button
            disabled={!playable || busyId === `${purchase_id}:download`}
            onClick={() => onOpen(purchase_id, 'download')}
            style={{ ...btn, background:'#1a1a1a', border:'1px solid #333' }}
          >
            {busyId === `${purchase_id}:download` ? '…' : 'Download'}
          </button>
        </div>
      </div>
    </div>
  );
}

const page = {
  minHeight:'100vh',
  background:'linear-gradient(180deg, #0f0f1e 0%, #0a0a0f 100%)',
};
const eyebrow = { fontSize: 11, color:'#888', letterSpacing: 2, textTransform:'uppercase' };
const grid = {
  display:'grid',
  gridTemplateColumns:'repeat(auto-fill, minmax(280px, 1fr))',
  gap: 20,
};
const card = {
  background:'#111',
  borderRadius: 12,
  border:'1px solid #222',
  overflow:'hidden',
};
const thumbWrap = { aspectRatio:'16/9', background:'#0a0a0a', position:'relative' };
const thumb     = { width:'100%', height:'100%', objectFit:'cover', display:'block' };
const badge     = {
  position:'absolute', top: 8, left: 8,
  padding:'4px 10px', borderRadius: 4,
  background:'rgba(251,191,36,0.15)', color:'#fbbf24',
  fontSize: 11, letterSpacing: 0.5,
};
const btn = {
  flex: 1, padding:'10px 0', borderRadius: 8, border:'none',
  color:'#fff', fontSize: 13, fontWeight: 600, cursor:'pointer',
};
const emptyBox = {
  background:'#111', borderRadius: 12, border:'1px solid #222',
  padding: 40, color:'#94a3b8', textAlign:'center', lineHeight: 1.6,
};
const signOutBtn = {
  padding:'8px 14px', borderRadius: 8, border:'1px solid #333',
  background:'transparent', color:'#94a3b8', fontSize: 13, cursor:'pointer',
};
